import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../menu-app/utils/api';
import toast from 'react-hot-toast';

export default function PublicMenu() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [lang, setLang] = useState('ar');
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [viewLayout, setViewLayout] = useState('list');
  // Grid view drill-state — null = show category tiles, otherwise = drilled into that category's items
  const [gridCatId, setGridCatId] = useState(null);
  const [offerPaused, setOfferPaused] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailItems, setDetailItems] = useState([]);   // sibling items for swipe nav
  const [detailIdx, setDetailIdx]   = useState(0);
  const [detailVIdx, setDetailVIdx] = useState(0);
  const [detailQty, setDetailQty]   = useState(1);
  const detailSwipeRef = useRef(null); // touch start X
  const [basket, setBasket] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(`mb_${token}`) || '[]'); } catch { return []; }
  });
  const [showBasket, setShowBasket] = useState(false);
  const [orderBranch, setOrderBranch] = useState(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderName, setOrderName] = useState(() => localStorage.getItem('pm_order_name') || '');
  const [orderAddress, setOrderAddress] = useState(() => localStorage.getItem('pm_order_address') || '');
  const [orderPhone, setOrderPhone] = useState(() => localStorage.getItem('pm_order_phone') || '');

  const sectionRefs = useRef({});
  const tabsRef = useRef(null);
  const isScrollingToRef = useRef(false);

  useEffect(() => {
    api.get(`/menu/${token}`)
      .then(res => {
        setData(res.data);
        const firstRegular = res.data.categories?.find(c => !c.is_offer);
        if (firstRegular) setActiveCategory(firstRegular.id);
        else if (res.data.categories?.length > 0) setActiveCategory(res.data.categories[0].id);
        // Load saved theme layout
        if (res.data.branch?.theme_layout) {
          setViewLayout(res.data.branch.theme_layout);
        }
        // Initial display language — set in QR Appearance row by the brand
        // owner. Customer can still toggle in-menu via the AR/EN button.
        const themeLang = res.data.branch?.theme_lang;
        if (themeLang === 'ar' || themeLang === 'en') {
          setLang(themeLang);
        }
        // Always start grid view at the category-tile screen (not drilled in)
        setGridCatId(null);
      })
      .catch(() => setError('Menu not found'))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-scroll active tab into view in the tabs bar. The browser's native
  // Element.scrollIntoView() with `inline: 'center'` correctly centers the
  // tab horizontally regardless of the bar's writing direction (LTR or RTL),
  // unlike manual scrollLeft math which has different semantics in RTL on
  // different browsers (some return negative values, some count from the
  // right edge). `block: 'nearest'` keeps the page from scrolling vertically.
  const scrollTabIntoView = useCallback((catId) => {
    const scroller = tabsRef.current;
    if (!scroller) return;
    const tab = scroller.querySelector(`[data-tab-id="${catId}"]`);
    if (!tab) return;
    try {
      tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    } catch {
      // Fallback for very old browsers — manual LTR math (RTL bug accepted)
      const tRect = tab.getBoundingClientRect();
      const sRect = scroller.getBoundingClientRect();
      const tabLeftInScroller = tRect.left - sRect.left + scroller.scrollLeft;
      const target = tabLeftInScroller - (scroller.clientWidth / 2) + (tRect.width / 2);
      const max    = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scroller.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
    }
  }, []);

  // Drag-suppression flag shared between the imperative drag listener
  // and the click-capture handler.
  const dragMovedRef = useRef(0);

  // Track active category while scrolling
  useEffect(() => {
    if (!data?.categories?.length) return;
    // In grid drill-down (viewLayout==='grid' && gridCatId set) only one
    // section is rendered, so the scroll-tracker would incorrectly fall back
    // to categories[0]. Skip the listener entirely; activeCategory is
    // controlled by gridCatId in that mode.
    if (viewLayout === 'grid') return;
    const categories = data.categories.filter(c => !c.is_offer);

    const handleScroll = () => {
      if (isScrollingToRef.current) return;
      const scrollY = window.scrollY + 130; // offset for sticky tabs height
      let currentCat = categories[0].id;
      for (const cat of categories) {
        const el = sectionRefs.current[cat.id];
        if (el && el.offsetTop <= scrollY) {
          currentCat = cat.id;
        }
      }
      setActiveCategory(prev => {
        if (prev !== currentCat) {
          scrollTabIntoView(currentCat);
          return currentCat;
        }
        return prev;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [data, scrollTabIntoView, viewLayout]);

  // In grid drill-down: keep the active tab in sync with the drilled-into
  // category and auto-scroll the tabs bar so it's visible.
  // Retry multiple times — the tabs bar transitions from hidden→sticky and
  // its scrollWidth/scrollLeft may not be finalized for a few frames,
  // especially for tabs deep in the list. Browser's native scrollIntoView
  // with inline:'center' is more reliable than computing scrollLeft manually.
  useEffect(() => {
    if (viewLayout === 'grid' && gridCatId) {
      setActiveCategory(gridCatId);
      const center = () => {
        const scroller = tabsRef.current;
        if (!scroller) return false;
        const tab = scroller.querySelector(`[data-tab-id="${gridCatId}"]`);
        if (!tab) return false;
        // scrollIntoView with inline:'center' centers horizontally;
        // block:'nearest' avoids vertical page scroll if the bar is in view.
        try {
          tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        } catch {
          scrollTabIntoView(gridCatId);
        }
        return true;
      };
      const t1 = setTimeout(center, 80);
      const t2 = setTimeout(center, 240);
      const t3 = setTimeout(center, 500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [gridCatId, viewLayout, scrollTabIntoView]);

  // Persist basket to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(`mb_${token}`, JSON.stringify(basket));
  }, [basket, token]);

  // Click a tab → smooth scroll to that section
  const scrollToCategory = (catId) => {
    setActiveCategory(catId);
    scrollTabIntoView(catId);
    const el = sectionRefs.current[catId];
    if (!el) return;
    isScrollingToRef.current = true;
    const offset = 110; // sticky tabs height + some breathing room
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    // Re-enable scroll tracking after animation
    setTimeout(() => { isScrollingToRef.current = false; }, 800);
  };


  // navDetail + keyboard nav — both before early returns (React hook rules)
  const navDetail = useCallback((navDir) => {
    setDetailIdx(prev => {
      const next = prev + navDir;
      if (next < 0 || next >= detailItems.length) return prev;
      const it = detailItems[next];
      setDetailItem(it);
      setDetailVIdx(it.variants?.length > 0 ? 0 : null);
      setDetailQty(1);
      return next;
    });
  }, [detailItems]);

  useEffect(() => {
    if (!detailItem) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setDetailItem(null); setTimeout(() => setOfferPaused(false), 500); return; }
      if (e.key === 'ArrowRight') navDetail(1);
      if (e.key === 'ArrowLeft')  navDetail(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailItem, navDetail]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 48 }}>🍽</div>
      <h2 style={{ marginTop: 16 }}>Menu not found</h2>
      <p style={{ color: '#757575' }}>This QR code may be invalid or expired</p>
    </div>
  );

  const { branch, categories, items, branches_with_maps = [], all_branches = [] } = data;
  const bgColor = branch.theme_bg_color || '#f5f5f5';
  const accentColor = branch.theme_accent_color || branch.brand_color || '#2196F3';
  const font = branch.font || 'Cairo';
  const showPhotos = branch.theme_show_photos !== false;
  const isAr = lang === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';
  const t = (en, ar) => (isAr && ar) ? ar : en;
  const formatPrice = (price) => {
    const num = parseFloat(price);
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };
  const currency = isAr ? 'جنيه' : 'EGP';
  const isLight = (hex) => {
    const h = (hex || '#fff').replace('#', '');
    if (h.length < 6) return true;
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  };
  const light = isLight(bgColor);
  const textColor = light ? '#111' : '#fff';
  const cardBg = light ? '#fff' : '#2A2A2A';
  const cardBorder = light ? '#E0E0E0' : '#444444';

  const offerCats   = categories.filter(c => c.is_offer);
  const regularCats = categories.filter(c => !c.is_offer);
  const allOfferItems = items.filter(i => offerCats.some(c => c.id === i.category_id));
  const waBranches = branches_with_maps.filter(b => b.whatsapp);

  // ── Basket helpers ────────────────────────────────────────────────────────
  const bKey = (itemId, vIdx) => `${itemId}-${vIdx ?? 'x'}`;
  const itemBasketQty = (itemId) => basket.filter(e => e.itemId === itemId).reduce((s, e) => s + e.qty, 0);
  const basketTotal   = basket.reduce((sum, e) => sum + e.price * e.qty, 0);
  const basketCount   = basket.reduce((sum, e) => sum + e.qty, 0);

  const addToBasket = (item, vIdx, qty = 1) => {
    const v = (vIdx !== null && vIdx !== undefined && item.variants?.length > 0) ? item.variants[vIdx] : null;
    const price = v ? parseFloat(v.price) : parseFloat(item.price || 0);
    const key = bKey(item.id, vIdx);
    setBasket(prev => {
      const ex = prev.find(e => e.key === key);
      if (ex) return prev.map(e => e.key === key ? { ...e, qty: e.qty + qty } : e);
      return [...prev, { key, itemId: item.id, name: item.name, name_ar: item.name_ar, icon: item.icon, image_thumb: item.image_thumb, image_path: item.image_path, variantName: v?.name || null, variantName_ar: v?.name_ar || null, price, qty }];
    });
  };

  const updateQty = (key, delta) => setBasket(prev =>
    prev.map(e => e.key === key ? { ...e, qty: e.qty + delta } : e).filter(e => e.qty > 0)
  );

  // SET qty (not add) — used from detail modal so repeated opens don't keep stacking
  const setBasketQty = (item, vIdx, qty) => {
    const v = (vIdx !== null && vIdx !== undefined && item.variants?.length > 0) ? item.variants[vIdx] : null;
    const price = v ? parseFloat(v.price) : parseFloat(item.price || 0);
    const key = bKey(item.id, vIdx);
    if (qty <= 0) {
      setBasket(prev => prev.filter(e => e.key !== key));
      return;
    }
    setBasket(prev => {
      const ex = prev.find(e => e.key === key);
      if (ex) return prev.map(e => e.key === key ? { ...e, qty } : e);
      return [...prev, { key, itemId: item.id, name: item.name, name_ar: item.name_ar, icon: item.icon, image_thumb: item.image_thumb, image_path: item.image_path, variantName: v?.name || null, variantName_ar: v?.name_ar || null, price, qty }];
    });
  };

  const openDetail = (item, categoryItems = []) => {
    const list = categoryItems.filter(i => i.id);
    const initVIdx = item.variants?.length > 0 ? 0 : null;
    const initKey = bKey(item.id, initVIdx);
    const existingQty = basket.find(e => e.key === initKey)?.qty || 0;
    setDetailItems(list);
    setDetailIdx(list.findIndex(i => i.id === item.id));
    setDetailItem(item);
    setDetailVIdx(initVIdx);
    setDetailQty(existingQty > 0 ? existingQty : 1);
  };

  const buildWAMsg = (name, address, phone) => {
    const ob = orderBranch || (waBranches.length === 1 ? waBranches[0] : null) || branch;
    const bn = isAr && ob.name_ar ? ob.name_ar : ob.name;
    const sep = '--------------------------------';
    let msg = '';
    if (isAr) {
      msg += '*طلب جديد*\n';
      msg += sep + '\n';
      msg += 'الفرع: ' + bn + '\n';
      if (name)    msg += 'الاسم: ' + name + '\n';
      if (phone)   msg += 'الهاتف: ' + phone + '\n';
      if (address) msg += 'العنوان: ' + address + '\n';
      msg += sep + '\n';
      basket.forEach((e, idx) => {
        const n = e.name_ar || e.name;
        const vl = (e.variantName_ar || e.variantName) ? ' (' + (e.variantName_ar || e.variantName) + ')' : '';
        msg += '*' + (idx + 1) + '. ' + n + vl + '*\n';
        msg += 'العدد (' + e.qty + ') = ' + formatPrice(e.price * e.qty) + ' جنيه\n';
      });
      msg += sep + '\n';
      msg += '*الاجمالي: ' + formatPrice(basketTotal) + ' جنيه*\n';
      msg += sep + '\n';
      msg += '_(ارسل من القائمة الرقمية)_';
    } else {
      msg += '*New Order*\n';
      msg += sep + '\n';
      msg += 'Branch: ' + bn + '\n';
      if (name)    msg += 'Name: ' + name + '\n';
      if (phone)   msg += 'Phone: ' + phone + '\n';
      if (address) msg += 'Address: ' + address + '\n';
      msg += sep + '\n';
      basket.forEach((e, idx) => {
        const n = e.name;
        const vl = e.variantName ? ' (' + e.variantName + ')' : '';
        msg += '*' + (idx + 1) + '. ' + n + vl + '*\n';
        msg += 'Qty (' + e.qty + ') = ' + formatPrice(e.price * e.qty) + ' ' + currency + '\n';
      });
      msg += sep + '\n';
      msg += '*Total: ' + formatPrice(basketTotal) + ' ' + currency + '*\n';
      msg += sep + '\n';
      msg += '_(sent from digital menu)_';
    }
    return encodeURIComponent(msg);
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: bgColor, fontFamily: 'Arial, sans-serif', direction: dir, color: textColor, paddingBottom: 80 }}>

      {/* ===== HEADER ===== */}
      <div style={{ background: accentColor, padding: '16px 16px', color: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, direction: 'ltr' }}>
          {branch.logo_path ? (
            <img
              src={branch.logo_path}
              alt="logo"
              style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'contain', background: 'white', flexShrink: 0, padding: 2 }}
            />
          ) : (
            <div style={{ width: 52, height: 52, borderRadius: 12, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 22, flexShrink: 0 }}>
              {(branch.brand_name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, direction: dir }}>
            <div style={{ fontWeight: 900, fontSize: 22, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: 0.2 }}>
              {t(branch.brand_name, branch.brand_name_ar)}
            </div>
            {/* Branch name on a tiny line below — only when this is a
                branch-level QR (brand-level QR has branch.name == brand.name).
                Uses a storefront icon to differentiate from the 📍 picker button. */}
            {branch.id && t(branch.name, branch.name_ar) && t(branch.name, branch.name_ar) !== t(branch.brand_name, branch.brand_name_ar) && (
              <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.85, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                🏬 {t(branch.name, branch.name_ar)}
              </div>
            )}
          </div>
          {(all_branches.length > 1 || branches_with_maps.length > 0) && (
            <button
              onClick={() => setShowMapPicker(true)}
              title={isAr ? 'اختر الفرع' : 'Choose a branch'}
              style={{
                width: 38, height: 38, borderRadius: 10,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
                border: '1.5px solid rgba(255,255,255,0.35)',
                cursor: 'pointer', transition: 'background 0.15s'
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >📍</button>
          )}
          <button
            onClick={() => setLang(l => l === 'ar' ? 'en' : 'ar')}
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: 'rgba(255,255,255,0.2)',
              border: '1.5px solid rgba(255,255,255,0.35)',
              color: 'white', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Arial, sans-serif',
              transition: 'background 0.15s'
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            {isAr ? 'EN' : 'ع'}
          </button>
        </div>
      </div>

      {/* ===== BRANCH MAP PICKER MODAL ===== */}
      {showMapPicker && (
        <div
          onClick={() => setShowMapPicker(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '20px 16px 32px', fontFamily: 'Arial, sans-serif' }}
          >
            {/* Handle bar */}
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#DDD', margin: '0 auto 18px' }} />
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14, textAlign: isAr ? 'right' : 'left', direction: dir }}>
              {isAr ? 'اختر الفرع' : 'Choose a Branch'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(all_branches.length > 0 ? all_branches : branches_with_maps).map(b => {
                const isCurrent = branch?.id && String(b.id) === String(branch.id);
                // Every branch with a QR token is tappable — including the
                // current one (acts as a refresh / no-op nav).
                const canSwitch = !!b.qr_token;
                const switchToBranch = () => {
                  if (!canSwitch) return;
                  setShowMapPicker(false);
                  window.location.assign(`/menu/${b.qr_token}`);
                };
                return (
                <div
                  key={b.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 14px', borderRadius: 12,
                    border: `1.5px solid ${isCurrent ? '#86EFAC' : '#E0E0E0'}`,
                    background: isCurrent ? '#F0FDF4' : '#FAFAFA',
                    direction: dir,
                  }}
                >
                  <div
                    onClick={switchToBranch}
                    role={canSwitch ? 'button' : undefined}
                    tabIndex={canSwitch ? 0 : undefined}
                    style={{
                      flex: 1, textAlign: isAr ? 'right' : 'left',
                      cursor: canSwitch ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                    onMouseOver={e => { if (canSwitch) e.currentTarget.style.opacity = '0.75'; }}
                    onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 15, color: isCurrent ? '#15803D' : '#1A1A1A', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {isAr && b.name_ar ? b.name_ar : b.name}
                      {isCurrent && (
                        <span style={{ fontSize: 10, fontWeight: 700, background: '#22C55E', color: '#fff', borderRadius: 10, padding: '1px 7px' }}>
                          {isAr ? 'الحالي' : 'CURRENT'}
                        </span>
                      )}
                    </div>
                    {b.name_ar && !isAr && <div style={{ fontSize: 12, color: '#999' }}>{b.name_ar}</div>}
                    {canSwitch && !isCurrent && (
                      <div style={{ fontSize: 11, color: '#16A34A', marginTop: 2, fontWeight: 600 }}>
                        {isAr ? 'اضغط لعرض منيو هذا الفرع ←' : '→ Tap to view this branch menu'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {b.phone && (
                      <a
                        href={`tel:${b.phone}`}
                        title={isAr ? 'اتصل' : 'Call'}
                        style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: '#F0FDF4', border: '1.5px solid #BBF7D0',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          textDecoration: 'none', fontSize: 18,
                        }}
                      >📞</a>
                    )}
                    {b.whatsapp && (
                      <a
                        href={`https://wa.me/${b.whatsapp.replace(/\D/g, '')}`}
                        target="_blank" rel="noreferrer"
                        title={isAr ? 'واتساب' : 'WhatsApp'}
                        style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: '#F0FDF4', border: '1.5px solid #86EFAC',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          textDecoration: 'none',
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </a>
                    )}
                    {b.google_maps_url && (
                      <button
                        onClick={() => { window.open(b.google_maps_url, '_blank'); setShowMapPicker(false); }}
                        title={isAr ? 'الموقع على الخريطة' : 'Open in Maps'}
                        style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: '#EFF6FF', border: '1.5px solid #BFDBFE',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', fontSize: 18,
                        }}
                      >📍</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== OFFERS BANNER ===== */}
      {allOfferItems.length > 0 && (
        <div style={{ paddingTop: 14, paddingBottom: 4 }}>
          <style>{`@keyframes offerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>

          {/* Strip heading */}
          <div style={{ padding: '0 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', direction: dir }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: textColor, fontFamily: 'Cairo, sans-serif', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 18 }}>🏷️</span>
              {isAr ? 'عروض خاصة' : 'Special Offers'}
            </span>
            <button
              onClick={() => setOfferPaused(p => !p)}
              style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${cardBorder}`, background: cardBg, color: textColor, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {offerPaused ? '▶' : '⏸'}
            </button>
          </div>

          {/* Scrolling strip */}
          <div
            style={{ overflow: 'hidden', direction: 'ltr', paddingLeft: 16 }}
            onMouseEnter={() => setOfferPaused(true)}
            onMouseLeave={() => setOfferPaused(false)}
            onTouchStart={() => setOfferPaused(true)}
            onTouchEnd={() => { setTimeout(() => { if (!detailItem) setOfferPaused(false); }, 3500); }}
          >
            <div style={{
              display: 'flex',
              width: 'max-content',
              animation: `offerScroll ${Math.max(allOfferItems.length * 3.5, 12)}s linear infinite`,
              animationPlayState: offerPaused ? 'paused' : 'running',
              willChange: 'transform',
            }}>
              {[...allOfferItems, ...allOfferItems].map((item, i) => (
                <div
                  key={i}
                  onClick={() => { setOfferPaused(true); openDetail(item, allOfferItems); }}
                  style={{
                    width: 148, height: 188, borderRadius: 14, overflow: 'hidden',
                    flexShrink: 0, marginRight: 10, cursor: 'pointer',
                    position: 'relative', background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                    transition: 'transform 0.15s',
                  }}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {showPhotos && item.image_path ? (
                    <img
                      src={item.image_thumb || item.image_path}
                      alt={t(item.name, item.name_ar)}
                      style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'center',
                        background: light ? '#f0f0f0' : '#1a1a1a',
                      }}
                    />
                  ) : showPhotos && item.icon && item.icon !== '🍽' ? (
                    <div style={{ width: '100%', height: '100%', background: light ? '#FFF8E1' : 'rgba(255,200,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 64 }}>
                      {item.icon}
                    </div>
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${accentColor}33, ${accentColor}11)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52, opacity: 0.7 }}>
                      🏷️
                    </div>
                  )}
                  {/* Gradient overlay with name + price */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 55%, transparent 100%)',
                    padding: '28px 10px 10px',
                    direction: dir, textAlign: isAr ? 'right' : 'left',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#fff', fontFamily: 'Cairo, sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>
                      {t(item.name, item.name_ar)}
                    </div>
                    <div style={{
                      display: 'inline-block',
                      background: 'rgba(255,255,255,0.93)',
                      borderRadius: 7, padding: '2px 8px',
                      fontWeight: 800, fontSize: 12, color: accentColor,
                    }}>
                      {item.variants?.length > 0
                        ? `${isAr ? 'من' : 'from'} ${formatPrice(Math.min(...item.variants.map(v => parseFloat(v.price))))} ${currency}`
                        : `${formatPrice(item.price)} ${currency}`
                      }
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom divider */}
          <div style={{ height: 1, background: cardBorder, margin: '12px 0 0' }} />
        </div>
      )}

      {/* ===== STICKY CATEGORY TABS ===== (hidden in Grid tile-browser mode) */}
      {!(viewLayout === 'grid' && !gridCatId) && (
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: bgColor, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div
          ref={(el) => {
            tabsRef.current = el;
            if (!el || el._dragWired) return;
            el._dragWired = true;
            // Imperative native-event drag wiring (React's onMouseDown was
            // unreliable here — events appeared to be swallowed before
            // reaching the handler on PC).
            let down = false, startX = 0, startSL = 0, moved = 0;
            const onMove = (ev) => {
              if (!down) return;
              const dx = ev.clientX - startX;
              if (Math.abs(dx) > 2) {
                moved = Math.max(moved, Math.abs(dx));
                dragMovedRef.current = moved;
                el.scrollLeft = startSL - dx;
                ev.preventDefault();
              }
            };
            const onUp = () => {
              down = false;
              el.style.cursor = 'grab';
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            el.addEventListener('mousedown', (e) => {
              if (e.button !== 0) return;
              down = true;
              startX = e.clientX;
              startSL = el.scrollLeft;
              moved = 0;
              dragMovedRef.current = 0;
              el.style.cursor = 'grabbing';
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            });
            // Vertical wheel → horizontal scroll on the bar
            el.addEventListener('wheel', (e) => {
              if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                el.scrollLeft += e.deltaY;
              }
            }, { passive: false });
          }}
          onClickCapture={(e) => {
            // Suppress the click that fires after a real drag so the tab
            // under the cursor isn't activated when the user was just
            // swiping the bar.
            if (dragMovedRef.current > 5) {
              e.stopPropagation();
              e.preventDefault();
              dragMovedRef.current = 0;
            }
          }}
          style={{
            overflowX: 'auto',
            display: 'flex',
            gap: 8,
            padding: '10px 16px',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            cursor: 'grab',
            userSelect: 'none',
            touchAction: 'pan-x',
            // RTL writing direction in Arabic so the FIRST tab is on the right
            // and tabs scroll right-to-left to match reading order.
            direction: dir,
          }}
        >
          {regularCats.map(cat => (
            <button
              key={cat.id}
              data-tab-id={cat.id}
              onClick={() => {
                if (viewLayout === 'grid') {
                  // In grid drill-down mode: switch to that category instead of scrolling
                  setGridCatId(cat.id);
                  setActiveCategory(cat.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  scrollToCategory(cat.id);
                }
              }}
              style={{
                padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                background: activeCategory === cat.id ? accentColor : cardBg,
                color: activeCategory === cat.id ? '#fff' : textColor,
                fontWeight: activeCategory === cat.id ? 600 : 400,
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)', fontSize: 14,
                fontFamily: 'Cairo, sans-serif', flexShrink: 0,
                transition: 'background 0.2s, color 0.2s',
                border: `1px solid ${activeCategory === cat.id ? 'transparent' : cardBorder}`
              }}
            >
              {t(cat.name, cat.name_ar)}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* ═════════ GRID-MODE CATEGORY BROWSER (2 tiles per row) ═════════ */}
      {viewLayout === 'grid' && !gridCatId && regularCats.length > 0 && (
        <div style={{ padding: '20px 14px 28px' }}>
          {/* Title strip */}
          <div style={{ padding: '0 4px 12px', direction: dir, textAlign: isAr ? 'right' : 'left' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: textColor, fontFamily: 'Cairo, sans-serif', letterSpacing: 0.2 }}>
              {isAr ? 'تصفح المنيو' : 'Browse Menu'}
            </div>
            <div style={{ fontSize: 12, color: light ? '#888' : 'rgba(255,255,255,0.55)', marginTop: 2, fontFamily: 'Cairo, sans-serif' }}>
              {regularCats.length} {isAr ? (regularCats.length === 1 ? 'قسم' : 'أقسام') : (regularCats.length === 1 ? 'category' : 'categories')}
            </div>
          </div>
          {/* 2-column grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, direction: dir }}>
          {regularCats.map(cat => {
            const catItemCount = items.filter(i => i.category_id === cat.id).length;
            if (catItemCount === 0) return null;
            const cover = cat.cover_image;
            // Pick 3 sample item thumbnails for the bottom teaser when no cover image
            const sampleThumbs = !cover
              ? items.filter(i => i.category_id === cat.id && i.image_thumb).slice(0, 3).map(i => i.image_thumb)
              : [];
            return (
              <button key={cat.id}
                onClick={() => { setGridCatId(cat.id); setActiveCategory(cat.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 18,
                  overflow: 'hidden',
                  // Tile bg = theme bg, so when the cover image is `object-fit: contain`,
                  // the area around it blends with the rest of the menu instead of
                  // showing a hardcoded black/grey frame. Cards with no cover stay
                  // on a soft accent-tinted gradient that still reads as part of theme.
                  background: cover
                    ? bgColor
                    : (light
                        ? `linear-gradient(135deg, ${bgColor} 0%, ${accentColor}22 100%)`
                        : `linear-gradient(135deg, ${bgColor} 0%, ${accentColor}33 100%)`),
                  boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
                  textAlign: isAr ? 'right' : 'left',
                  direction: dir,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  transition: 'transform 0.18s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.26)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,0.16)'; }}
              >
                {/* Cover image — fills tile (cover). Photo's own bg may show
                    edge-to-edge; the frosted-glass title frame below keeps text
                    readable regardless. */}
                {cover && (
                  <img src={cover} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                {/* Teaser stack of item thumbs when no cover */}
                {!cover && sampleThumbs.length > 0 && (
                  <div style={{ position: 'absolute', top: '20%', left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    {sampleThumbs.map((src, i) => (
                      <img key={i} src={src} alt=""
                        style={{
                          width: 64, height: 64, borderRadius: '50%',
                          objectFit: 'cover',
                          border: `3px solid ${light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.18)'}`,
                          background: light ? '#fff' : '#1f1f1f',
                          marginLeft: i === 0 ? 0 : -16,
                          transform: `rotate(${(i - 1) * 6}deg)`,
                          boxShadow: '0 4px 10px rgba(0,0,0,0.20)',
                        }} />
                    ))}
                  </div>
                )}
                {/* Item count chip — frosted, themed */}
                <div style={{
                  position: 'absolute', top: 10, [isAr ? 'left' : 'right']: 10, zIndex: 2,
                  background: light ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                  color: textColor,
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 12,
                  border: `1px solid ${light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.18)'}`,
                  fontFamily: 'Cairo, sans-serif', letterSpacing: 0.3,
                }}>
                  {catItemCount} {isAr ? (catItemCount === 1 ? 'صنف' : 'أصناف') : (catItemCount === 1 ? 'item' : 'items')}
                </div>
                {/* Frosted title frame — a translucent themed panel sits over
                    the photo so the text is always readable, no matter the
                    photo's content or its own background. */}
                <div style={{
                  position: 'absolute',
                  left: 10, right: 10, bottom: 10,
                  zIndex: 1,
                  padding: '8px 12px 9px',
                  borderRadius: 12,
                  background: light ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: `1px solid ${light ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.10)'}`,
                  boxShadow: light ? '0 4px 12px rgba(0,0,0,0.10)' : '0 4px 12px rgba(0,0,0,0.35)',
                  color: textColor,
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                  <div style={{
                    fontSize: 15, fontWeight: 800, fontFamily: 'Cairo, sans-serif',
                    lineHeight: 1.25, letterSpacing: 0.2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t(cat.name, cat.name_ar)}
                  </div>
                  <div style={{ fontSize: 10.5, opacity: 0.95, fontWeight: 700, fontFamily: 'Cairo, sans-serif' }}>
                    <span style={{ color: accentColor }}>{isAr ? 'تصفح' : 'Browse'} {isAr ? '←' : '→'}</span>
                  </div>
                </div>
              </button>
            );
          })}
          </div>
        </div>
      )}

      {/* (Grid drill-down: header is rendered as the cover banner inside the
          category section below, so we don't render a separate top bar here.) */}


      {/* ===== ALL CATEGORY SECTIONS ===== */}
      {regularCats.map(cat => {
        // In Grid mode: only render the currently-drilled-into category
        if (viewLayout === 'grid' && cat.id !== gridCatId) return null;
        const catItems = items.filter(i => i.category_id === cat.id);
        if (catItems.length === 0) return null;
        const cover = cat.cover_image;
        const sampleThumbs = !cover
          ? catItems.filter(i => i.image_thumb).slice(0, 3).map(i => i.image_thumb)
          : [];
        const inGridDrill = viewLayout === 'grid' && cat.id === gridCatId;
        return (
          <div
            key={cat.id}
            ref={el => { sectionRefs.current[cat.id] = el; }}
            style={{ paddingTop: inGridDrill ? 12 : 18 }}
          >
            {/* ── Category cover banner (used by both list & grid drill-down) ── */}
            <div style={{ padding: '0 14px 14px' }}>
              <div style={{
                position: 'relative',
                width: '100%',
                minHeight: 96,
                borderRadius: 18,
                overflow: 'hidden',
                // Banner bg follows the active theme — cover photos sit `contain`'d
                // inside this color, no more hardcoded black/dark frame.
                background: cover
                  ? bgColor
                  : (light
                      ? `linear-gradient(135deg, ${bgColor} 0%, ${accentColor}1F 100%)`
                      : `linear-gradient(135deg, ${bgColor} 0%, ${accentColor}33 100%)`),
                boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                display: 'flex',
                alignItems: 'stretch',
                direction: dir,
              }}>
                {/* Cover image — fills the banner. Frosted title frame below
                    keeps text readable regardless of the photo's content/bg. */}
                {cover && (
                  <img src={cover} alt=""
                       style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                {/* Teaser thumbs when no cover */}
                {!cover && sampleThumbs.length > 0 && (
                  <div style={{ position: 'absolute', top: 0, bottom: 0, [isAr ? 'left' : 'right']: 0, width: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {sampleThumbs.map((src, i) => (
                      <img key={i} src={src} alt=""
                        style={{
                          width: 60, height: 60, borderRadius: '50%',
                          objectFit: 'cover',
                          border: `3px solid ${light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.18)'}`,
                          background: light ? '#fff' : '#1f1f1f',
                          marginInlineStart: i === 0 ? 0 : -18,
                          transform: `rotate(${(i - 1) * 5}deg)`,
                          boxShadow: '0 4px 10px rgba(0,0,0,0.20)',
                        }} />
                    ))}
                  </div>
                )}
                {/* Content layer — anchored to the text side. Title sits in a
                    frosted-glass card so it's legible no matter the photo. */}
                <div style={{
                  position: 'relative', zIndex: 1, flex: 1,
                  padding: '14px 18px',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  gap: 4,
                  textAlign: isAr ? 'right' : 'left',
                }}>
                  {/* Back button — only in grid drill-down */}
                  {inGridDrill && (
                    <button onClick={() => setGridCatId(null)}
                      style={{
                        alignSelf: isAr ? 'flex-end' : 'flex-start',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', borderRadius: 18,
                        border: `1.5px solid ${light ? 'rgba(0,0,0,0.18)' : 'rgba(255,255,255,0.45)'}`,
                        background: light ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.45)',
                        backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                        color: textColor, cursor: 'pointer',
                        fontSize: 11, fontWeight: 700, fontFamily: 'Cairo, sans-serif', marginBottom: 6
                      }}>
                      {isAr ? '→' : '←'} {isAr ? 'كل الأقسام' : 'All categories'}
                    </button>
                  )}
                  {/* Frosted title card */}
                  <div style={{
                    alignSelf: isAr ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    padding: '8px 14px 10px',
                    borderRadius: 14,
                    background: light ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: `1px solid ${light ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.10)'}`,
                    boxShadow: light ? '0 4px 12px rgba(0,0,0,0.10)' : '0 4px 12px rgba(0,0,0,0.35)',
                    color: textColor,
                  }}>
                    <div style={{ fontSize: inGridDrill ? 22 : 19, fontWeight: 800, fontFamily: 'Cairo, sans-serif', lineHeight: 1.2, letterSpacing: 0.2 }}>
                      {t(cat.name, cat.name_ar)}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.92, fontWeight: 600, letterSpacing: 0.3, marginTop: 2 }}>
                      {catItems.length} {isAr ? (catItems.length === 1 ? 'صنف' : 'أصناف') : (catItems.length === 1 ? 'item' : 'items')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Items */}
            <div style={{ padding: '0 16px', display: viewLayout === 'grid' ? 'grid' : 'flex', gridTemplateColumns: viewLayout === 'grid' ? 'repeat(2, 1fr)' : undefined, flexDirection: viewLayout === 'list' ? 'column' : undefined, gap: 12 }}>
              {catItems.map(item => {
                const inBasket = itemBasketQty(item.id);
                const allSamePrice = item.variants?.length > 0 && item.variants.every(v => String(v.price) === String(item.variants[0].price));
                return (
                <div key={item.id} style={{ position: 'relative' }} onClick={() => openDetail(item, catItems)}>
                <div style={{ background: cardBg, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', display: 'flex', flexDirection: viewLayout === 'grid' ? 'column' : 'row', border: `1px solid ${inBasket ? accentColor : cardBorder}`, cursor: 'pointer', transition: 'border-color 0.2s' }}>
                  {showPhotos && (item.image_path ? (
                    <img src={item.image_thumb || item.image_path} alt={item.name} style={{ width: viewLayout === 'grid' ? '100%' : 80, height: viewLayout === 'grid' ? 80 : 80, objectFit: 'contain', flexShrink: 0, background: light ? '#f9f9f9' : 'rgba(0,0,0,0.2)' }} />
                  ) : item.icon && item.icon !== '🍽' ? (
                    <div style={{ width: viewLayout === 'grid' ? '100%' : 80, height: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, background: light ? '#f9f9f9' : 'rgba(0,0,0,0.2)' }}>
                      {item.icon}
                    </div>
                  ) : branch.logo_path ? (
                    <img src={branch.logo_path} alt="" style={{ width: viewLayout === 'grid' ? '100%' : 80, height: 80, objectFit: 'contain', flexShrink: 0, background: light ? '#f9f9f9' : 'rgba(0,0,0,0.2)', padding: 10, opacity: 0.4 }} />
                  ) : (
                    <div style={{ width: viewLayout === 'grid' ? '100%' : 80, height: 80, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, background: light ? '#f9f9f9' : 'rgba(0,0,0,0.2)', color: accentColor, opacity: 0.5 }}>
                      {(branch.brand_name || '?').charAt(0).toUpperCase()}
                    </div>
                  ))}
                  <div style={{ padding: viewLayout === 'grid' ? '8px 10px' : '12px 14px', flex: 1, direction: dir, textAlign: viewLayout === 'grid' ? 'center' : (isAr ? 'right' : 'left') }}>
                    {/* Item name. In grid view we now WRAP across up to 2
                        lines (was nowrap+ellipsis, which truncated names like
                        "Nutriversum Basic Creatine Monohydrate" mid-word).
                        In list view it already wraps. */}
                    <div style={{
                      fontWeight: 600,
                      fontSize: viewLayout === 'grid' ? (showPhotos ? 13 : 15) : (showPhotos ? 15 : 17),
                      color: textColor,
                      overflow: 'hidden',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      // In grid mode, cap at 2 lines so cards stay aligned
                      ...(viewLayout === 'grid' ? {
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.25,
                        minHeight: '2.5em',     // reserve 2 lines to keep card heights even
                      } : {}),
                      fontFamily: 'Cairo, sans-serif',
                      textAlign: isAr ? 'right' : 'left',
                      marginBottom: 4,
                    }}>
                      {t(item.name, item.name_ar)}
                    </div>
                    {/* Descriptions are no longer shown in the list — they
                        appear when the user taps the item to open the detail
                        sheet. Keeps the list clean and lets every item card
                        be the same height. */}
                    {item.variants && item.variants.length > 0 ? (
                      allSamePrice ? (
                        /* ── Same price for all variants: show price like a regular item ── */
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexDirection: isAr ? 'row-reverse' : 'row' }}>
                            <button
                              onClick={e => { e.stopPropagation(); openDetail(item, catItems); }}
                              style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: accentColor, color: '#fff', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}
                            >＋</button>
                            <span style={{ fontWeight: 700, color: accentColor, fontSize: viewLayout === 'grid' ? 13 : 15 }}>{formatPrice(item.variants[0].price)} {currency}</span>
                            {viewLayout === 'list' && item.is_special && (
                              <span style={{ fontSize: 11, background: '#FFF3E0', color: '#E65100', padding: '2px 8px', borderRadius: 10, marginInlineStart: 'auto' }}>
                                {isAr ? 'مميز' : 'Special'}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4, justifyContent: viewLayout === 'grid' ? 'center' : (isAr ? 'flex-end' : 'flex-start'), direction: isAr ? 'rtl' : 'ltr' }}>
                            {item.variants.map((v, i) => (
                              <span key={i} style={{ fontSize: 11, background: light ? '#F0F4FF' : 'rgba(255,255,255,0.1)', color: light ? '#555' : 'rgba(255,255,255,0.75)', padding: '2px 8px', borderRadius: 12, fontWeight: 500 }}>
                                {t(v.name, v.name_ar)}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        /* ── Different prices per variant ── */
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4, justifyContent: viewLayout === 'grid' ? 'center' : (isAr ? 'flex-end' : 'flex-start'), direction: isAr ? 'rtl' : 'ltr' }}>
                          {viewLayout === 'grid' ? (
                            item.variants.length === 2 ? (
                              <div style={{ display: 'flex', gap: 4, flexDirection: 'row', width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {item.variants.map((v, i) => (
                                  <div key={i} style={{ fontSize: 10, fontWeight: 600, color: accentColor, textAlign: isAr ? 'right' : 'left' }}>
                                    {t(v.name, v.name_ar)}: {formatPrice(v.price)} {currency}
                                  </div>
                                ))}
                              </div>
                            ) : item.variants.length > 2 ? (
                              <div
                                onClick={() => openDetail(item, catItems)}
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: accentColor,
                                  cursor: 'pointer',
                                  padding: '4px 8px',
                                  borderRadius: 8,
                                  background: light ? '#E3F2FD' : 'rgba(33,150,243,0.2)',
                                  transition: 'all 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.background = light ? '#BBDEFB' : 'rgba(33,150,243,0.3)'}
                                onMouseOut={e => e.currentTarget.style.background = light ? '#E3F2FD' : 'rgba(33,150,243,0.2)'}
                              >
                                {isAr ? 'الأسعار' : 'Prices'}
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, fontWeight: 700, color: accentColor, textAlign: isAr ? 'right' : 'left', display: 'block', width: '100%' }}>
                                {formatPrice(item.variants[0].price)} {currency}
                              </span>
                            )
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flexDirection: isAr ? 'row-reverse' : 'row' }}>
                              <button
                                onClick={e => { e.stopPropagation(); openDetail(item, catItems); }}
                                style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: accentColor, color: '#fff', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}
                              >＋</button>
                              {item.variants.map((v, i) => (
                                <span key={i} style={{ fontSize: 12, background: light ? '#E3F2FD' : 'rgba(33,150,243,0.2)', color: accentColor, padding: '2px 8px', borderRadius: 12, fontWeight: 500, direction: isAr ? 'rtl' : 'ltr', textAlign: isAr ? 'right' : 'left' }}>
                                  {t(v.name, v.name_ar)}: {formatPrice(v.price)} {currency}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexDirection: isAr ? 'row-reverse' : 'row' }}>
                        <button
                          onClick={e => { e.stopPropagation(); addToBasket(item, null); toast.success(isAr ? 'أُضيف للسلة' : 'Added to basket', { duration: 1200 }); }}
                          style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: accentColor, color: '#fff', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}
                        >＋</button>
                        <span style={{ fontWeight: 700, color: accentColor, fontSize: viewLayout === 'grid' ? 13 : 15 }}>{formatPrice(item.price)} {currency}</span>
                        {viewLayout === 'list' && item.is_special && (
                          <span style={{ fontSize: 11, background: '#FFF3E0', color: '#E65100', padding: '2px 8px', borderRadius: 10, marginInlineStart: 'auto' }}>
                            {isAr ? 'مميز' : 'Special'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* Basket qty badge */}
                {inBasket > 0 && (
                  <div style={{ position: 'absolute', top: 6, right: isAr ? undefined : 6, left: isAr ? 6 : undefined, background: accentColor, color: '#fff', borderRadius: 10, minWidth: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, padding: '0 5px', pointerEvents: 'none', zIndex: 2 }}>
                    {inBasket}
                  </div>
                )}
                </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ═════════ GRID-MODE drill-down: prev / next category nav ═════════ */}
      {viewLayout === 'grid' && gridCatId && (() => {
        const drillIdx = regularCats.findIndex(c => c.id === gridCatId);
        const prevCat = drillIdx > 0 ? regularCats[drillIdx - 1] : null;
        const nextCat = drillIdx >= 0 && drillIdx < regularCats.length - 1 ? regularCats[drillIdx + 1] : null;
        const goCat = (id) => { setGridCatId(id); setActiveCategory(id); window.scrollTo({ top: 0, behavior: 'smooth' }); };
        // For RTL Arabic: prev/next visually swap so the arrows match reading direction
        const leftCat  = isAr ? nextCat : prevCat;
        const rightCat = isAr ? prevCat : nextCat;
        return (
          <div style={{ padding: '20px 14px 28px', display: 'flex', alignItems: 'stretch', gap: 10, direction: dir }}>
            {/* Left button */}
            <button onClick={() => leftCat && goCat(leftCat.id)} disabled={!leftCat}
              style={{ flex: 1, padding: '12px 14px', borderRadius: 14, border: `1.5px solid ${cardBorder}`, background: leftCat ? cardBg : 'transparent', color: leftCat ? textColor : (light ? '#bbb' : '#555'), cursor: leftCat ? 'pointer' : 'default', fontFamily: 'Cairo, sans-serif', textAlign: isAr ? 'right' : 'left', display: 'flex', alignItems: 'center', gap: 8, opacity: leftCat ? 1 : 0.45, transition: 'all 0.15s' }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: accentColor }}>←</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{isAr ? (rightCat === prevCat ? 'التالي' : 'السابق') : 'Prev'}</div>
                <div style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{leftCat ? t(leftCat.name, leftCat.name_ar) : '—'}</div>
              </div>
            </button>
            {/* Home (back to categories) */}
            <button onClick={() => setGridCatId(null)}
              style={{ flexShrink: 0, padding: '12px 16px', borderRadius: 14, border: 'none', background: accentColor, color: '#fff', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, fontWeight: 700, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              <span style={{ fontSize: 18 }}>⌂</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4 }}>{isAr ? 'الأقسام' : 'Menu'}</span>
            </button>
            {/* Right button */}
            <button onClick={() => rightCat && goCat(rightCat.id)} disabled={!rightCat}
              style={{ flex: 1, padding: '12px 14px', borderRadius: 14, border: `1.5px solid ${cardBorder}`, background: rightCat ? cardBg : 'transparent', color: rightCat ? textColor : (light ? '#bbb' : '#555'), cursor: rightCat ? 'pointer' : 'default', fontFamily: 'Cairo, sans-serif', textAlign: isAr ? 'left' : 'right', display: 'flex', alignItems: 'center', gap: 8, opacity: rightCat ? 1 : 0.45, transition: 'all 0.15s' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>{isAr ? (leftCat === prevCat ? 'التالي' : 'السابق') : 'Next'}</div>
                <div style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rightCat ? t(rightCat.name, rightCat.name_ar) : '—'}</div>
              </div>
              <span style={{ fontSize: 22, fontWeight: 700, color: accentColor }}>→</span>
            </button>
          </div>
        );
      })()}

      {/* ===== ITEM DETAIL MODAL ===== */}
      {detailItem && (() => {
        const hasPrev = detailIdx > 0;
        const hasNext = detailIdx < detailItems.length - 1;
        const currentPrice = detailVIdx !== null
          ? parseFloat(detailItem.variants?.[detailVIdx]?.price || 0)
          : parseFloat(detailItem.price || 0);
        const ArrowBtn = ({ left, onClick: onClickArrow, show }) => show ? (
          <button
            onClick={e => { e.stopPropagation(); onClickArrow(); }}
            style={{
              position: 'absolute', top: '50%', transform: 'translateY(-50%)',
              [left ? 'left' : 'right']: 10,
              width: 38, height: 38, borderRadius: '50%',
              background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
              border: '1.5px solid rgba(255,255,255,0.2)',
              color: '#fff', fontSize: 22, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 3, lineHeight: 1, padding: 0,
            }}
          >{left ? '‹' : '›'}</button>
        ) : null;

        return (
          <div
            onClick={() => { setDetailItem(null); setTimeout(() => setOfferPaused(false), 500); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          >
            <style>{`
              @keyframes _detailIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
            `}</style>
            <div
              onClick={e => e.stopPropagation()}
              onTouchStart={e => { detailSwipeRef.current = e.touches[0].clientX; }}
              onTouchEnd={e => {
                if (detailSwipeRef.current === null) return;
                const dx = detailSwipeRef.current - e.changedTouches[0].clientX;
                detailSwipeRef.current = null;
                if (Math.abs(dx) < 55) return;
                navDetail(dx > 0 ? 1 : -1);
              }}
              style={{ background: cardBg, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.4)', direction: dir }}
            >
              {/* Drag handle */}
              <div style={{ width: 40, height: 4, borderRadius: 2, background: light ? '#DDD' : '#555', margin: '14px auto 0' }} />

              {/* ── Photo area with navigation arrows ── */}
              <div style={{ position: 'relative', width: '100%', marginTop: 10, background: light ? '#f2f2f2' : '#141414', minHeight: showPhotos && (detailItem.image_path || (detailItem.icon && detailItem.icon !== '🍽')) ? undefined : 0 }}>
                {showPhotos && detailItem.image_path ? (
                  <img
                    key={`photo-${detailItem.id}`}
                    src={detailItem.image_path}
                    alt={t(detailItem.name, detailItem.name_ar)}
                    style={{
                      display: 'block', width: '100%', height: 'auto',
                      maxHeight: 320, objectFit: 'contain',
                      animation: '_detailIn 0.22s ease',
                    }}
                  />
                ) : showPhotos && detailItem.icon && detailItem.icon !== '🍽' ? (
                  <div key={`icon-${detailItem.id}`} style={{ width: '100%', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 88, animation: '_detailIn 0.22s ease' }}>
                    {detailItem.icon}
                  </div>
                ) : null}

                {/* Prev / Next arrows — overlaid on photo */}
                <ArrowBtn left show={hasPrev} onClick={() => navDetail(-1)} />
                <ArrowBtn left={false} show={hasNext} onClick={() => navDetail(1)} />

                {/* Dot indicators */}
                {detailItems.length > 1 && (
                  <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5, pointerEvents: 'none' }}>
                    {detailItems.map((_, i) => (
                      <div key={i} style={{ width: i === detailIdx ? 18 : 6, height: 6, borderRadius: 3, background: i === detailIdx ? accentColor : 'rgba(255,255,255,0.55)', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Item info (animated on item change) ── */}
              <div key={`info-${detailItem.id}`} style={{ padding: '16px 18px 8px', animation: '_detailIn 0.22s ease' }}>
                {/* Name */}
                <div style={{ fontWeight: 700, fontSize: 20, color: textColor, fontFamily: 'Cairo, sans-serif', lineHeight: 1.3, marginBottom: 6 }}>
                  {t(detailItem.name, detailItem.name_ar)}
                </div>
                {/* Description */}
                {t(detailItem.description, detailItem.description_ar) && (
                  <div style={{ fontSize: 13, color: light ? '#666' : 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: 14, fontFamily: 'Arial, sans-serif', fontWeight: 400 }}>
                    {t(detailItem.description, detailItem.description_ar)}
                  </div>
                )}

                {/* Variants or single price */}
                {detailItem.variants?.length > 0 ? (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: light ? '#999' : 'rgba(255,255,255,0.5)', marginBottom: 8, fontWeight: 600 }}>
                      {isAr ? 'اختر الحجم / النوع' : 'Choose size / type'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {detailItem.variants.map((v, i) => (
                        <div key={i} onClick={() => { setDetailVIdx(i); const k = bKey(detailItem.id, i); const eq = basket.find(e => e.key === k)?.qty || 0; setDetailQty(eq > 0 ? eq : 1); }}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: 12, border: `2px solid ${detailVIdx === i ? accentColor : cardBorder}`, background: detailVIdx === i ? (light ? `${accentColor}15` : `${accentColor}22`) : 'transparent', cursor: 'pointer', transition: 'all 0.15s' }}>
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: textColor, fontSize: 14 }}>{t(v.name, v.name_ar)}</span>
                          <span style={{ fontWeight: 800, color: accentColor, fontSize: 16 }}>{formatPrice(v.price)} {currency}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontWeight: 800, fontSize: 24, color: accentColor, marginBottom: 16 }}>
                    {formatPrice(detailItem.price)} {currency}
                  </div>
                )}

                {/* Qty counter */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 18 }}>
                  <button onClick={() => setDetailQty(q => Math.max(0, q - 1))}
                    style={{ width: 40, height: 40, borderRadius: 12, border: `1.5px solid ${detailQty === 1 ? cardBorder : '#EF4444'}`, background: detailQty === 1 ? 'transparent' : '#FFF5F5', color: detailQty === 1 ? textColor : '#EF4444', fontSize: 20, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>−</button>
                  <span style={{ fontSize: 22, fontWeight: 800, color: detailQty === 0 ? '#EF4444' : textColor, minWidth: 32, textAlign: 'center' }}>{detailQty}</span>
                  <button onClick={() => setDetailQty(q => q + 1)}
                    style={{ width: 40, height: 40, borderRadius: 12, border: `1.5px solid ${cardBorder}`, background: 'transparent', color: textColor, fontSize: 20, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>＋</button>
                </div>

                {/* Buttons row */}
                <div style={{ display: 'flex', gap: 10, paddingBottom: 20 }}>
                  <button onClick={() => { setDetailItem(null); setTimeout(() => setOfferPaused(false), 500); }}
                    style={{ flex: 1, height: 48, borderRadius: 14, border: `1.5px solid ${cardBorder}`, background: 'transparent', color: textColor, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                    {isAr ? '✕ إغلاق' : '✕ Close'}
                  </button>
                  <button
                    onClick={() => {
                      setBasketQty(detailItem, detailVIdx, detailQty);
                      if (detailQty === 0) {
                        toast.success(isAr ? 'تم الحذف من السلة 🗑' : 'Removed from basket 🗑', { duration: 1200 });
                      } else {
                        toast.success(isAr ? 'تم تحديث السلة 🛒' : 'Basket updated 🛒', { duration: 1200 });
                      }
                      setDetailItem(null);
                      setTimeout(() => setOfferPaused(false), 500);
                    }}
                    style={{ flex: 2, height: 48, borderRadius: 14, border: 'none', background: detailQty === 0 ? '#EF4444' : accentColor, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'Cairo, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.2s' }}>
                    {detailQty === 0
                      ? (isAr ? '🗑 إزالة من السلة' : '🗑 Remove from Basket')
                      : (isAr ? `🛒 أضف للسلة — ${formatPrice(currentPrice * detailQty)} ${currency}` : `🛒 Add to Basket — ${formatPrice(currentPrice * detailQty)} ${currency}`)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== BASKET SHEET ===== */}
      {showBasket && (
        <div onClick={() => setShowBasket(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1001, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: cardBg, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.4)', direction: dir }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: light ? '#DDD' : '#555', margin: '14px auto 10px' }} />
            <div style={{ padding: '4px 18px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: textColor, fontFamily: 'Cairo, sans-serif' }}>
                🛒 {isAr ? 'سلة الطلب' : 'Your Basket'}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => { if (window.confirm(isAr ? 'مسح السلة؟' : 'Clear basket?')) setBasket([]); }}
                  style={{ fontSize: 12, color: '#EF4444', background: 'none', border: '1px solid #FFCDD2', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}>
                  {isAr ? 'مسح الكل' : 'Clear all'}
                </button>
                <button onClick={() => setShowBasket(false)}
                  style={{ width: 30, height: 30, borderRadius: 8, border: `1.5px solid ${cardBorder}`, background: 'transparent', color: textColor, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
              </div>
            </div>

            {/* Items */}
            <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {basket.map(e => (
                <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: light ? '#F9F9F9' : 'rgba(255,255,255,0.06)', borderRadius: 12, border: `1px solid ${cardBorder}` }}>
                  {/* Thumbnail */}
                  {showPhotos && (e.image_thumb || e.image_path) ? (
                    <img src={e.image_thumb || e.image_path} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                  ) : showPhotos && e.icon && e.icon !== '🍽' ? (
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: light ? '#FFF8E1' : 'rgba(255,200,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{e.icon}</div>
                  ) : null}
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: textColor, fontFamily: 'Cairo, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isAr && e.name_ar ? e.name_ar : e.name}
                    </div>
                    {e.variantName && (
                      <div style={{ fontSize: 11, color: light ? '#888' : 'rgba(255,255,255,0.5)' }}>
                        {isAr && e.variantName_ar ? e.variantName_ar : e.variantName}
                      </div>
                    )}
                    <div style={{ fontWeight: 700, color: accentColor, fontSize: 13 }}>
                      {formatPrice(e.price * e.qty)} {currency}
                    </div>
                  </div>
                  {/* Qty controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => updateQty(e.key, -1)}
                      style={{ width: 28, height: 28, borderRadius: 8, border: `1.5px solid ${cardBorder}`, background: 'transparent', color: textColor, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                    <span style={{ fontSize: 15, fontWeight: 700, color: textColor, minWidth: 18, textAlign: 'center' }}>{e.qty}</span>
                    <button onClick={() => updateQty(e.key, 1)}
                      style={{ width: 28, height: 28, borderRadius: 8, border: `1.5px solid ${cardBorder}`, background: 'transparent', color: textColor, fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>＋</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Total + Send */}
            <div style={{ padding: '4px 18px 32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderTop: `1px solid ${cardBorder}`, marginBottom: 14 }}>
                <span style={{ fontWeight: 700, fontSize: 16, color: textColor, fontFamily: 'Cairo, sans-serif' }}>{isAr ? 'الإجمالي' : 'Total'}</span>
                <span style={{ fontWeight: 800, fontSize: 20, color: accentColor }}>{formatPrice(basketTotal)} {currency}</span>
              </div>

              {/* Branch picker */}
              {waBranches.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: light ? '#888' : 'rgba(255,255,255,0.5)', marginBottom: 8, fontWeight: 600, fontFamily: 'Cairo, sans-serif' }}>
                    {isAr ? 'اختر الفرع' : 'Choose branch'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {waBranches.map(b => {
                      const isSel = orderBranch ? orderBranch.id === b.id : waBranches.length === 1;
                      return (
                        <div key={b.id} onClick={() => setOrderBranch(b)}
                          style={{ padding: '10px 14px', borderRadius: 12, border: `2px solid ${isSel ? accentColor : cardBorder}`, background: isSel ? (light ? accentColor + '18' : accentColor + '28') : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s' }}>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${isSel ? accentColor : (light ? '#BBB' : '#555')}`, background: isSel ? accentColor : 'transparent', flexShrink: 0 }} />
                          <span style={{ fontFamily: 'Cairo, sans-serif', fontWeight: 600, color: textColor, fontSize: 14 }}>
                            {isAr && b.name_ar ? b.name_ar : b.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Send button */}
              {(() => {
                const target = orderBranch || (waBranches.length === 1 ? waBranches[0] : null);
                const needsPick = waBranches.length > 1 && !orderBranch;
                if (needsPick) {
                  return (
                    <div style={{ textAlign: 'center', padding: '12px 0', color: light ? '#999' : 'rgba(255,255,255,0.4)', fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>
                      {isAr ? 'اختر الفرع لإرسال الطلب' : 'Select a branch to send your order'}
                    </div>
                  );
                }
                if (target && target.whatsapp) {
                  return (
                    <button
                      onClick={() => { setShowOrderForm(true); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '15px 0', borderRadius: 16, background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 16, border: 'none', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                      <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      {isAr ? 'أرسل الطلب عبر واتساب' : 'Send Order via WhatsApp'}
                    </button>
                  );
                }
                return (
                  <div style={{ textAlign: 'center', padding: '12px 0', color: light ? '#999' : 'rgba(255,255,255,0.4)', fontSize: 13, fontFamily: 'Cairo, sans-serif' }}>
                    {isAr ? 'لا يتوفر رقم واتساب' : 'No WhatsApp number available'}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ===== ORDER INFO FORM ===== */}
      {showOrderForm && (
        <div onClick={() => setShowOrderForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: cardBg, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '0 0 32px', boxShadow: '0 -8px 40px rgba(0,0,0,0.4)', direction: dir }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: light ? '#DDD' : '#555', margin: '14px auto 16px' }} />
            <div style={{ padding: '0 18px' }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: textColor, fontFamily: 'Cairo, sans-serif', marginBottom: 6 }}>
                {isAr ? 'بيانات الطلب' : 'Order Details'}
              </div>
              <div style={{ fontSize: 13, color: light ? '#888' : 'rgba(255,255,255,0.5)', marginBottom: 18, fontFamily: 'Cairo, sans-serif' }}>
                {isAr ? 'ستُضاف هذه البيانات لرسالة الطلب' : 'This info will be included in your order message'}
              </div>

              {/* Name */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: light ? '#555' : 'rgba(255,255,255,0.6)', marginBottom: 5, fontFamily: 'Cairo, sans-serif' }}>
                  👤 {isAr ? 'الاسم' : 'Name'}
                </label>
                <input
                  type="text"
                  value={orderName}
                  onChange={e => setOrderName(e.target.value)}
                  placeholder={isAr ? 'اسمك' : 'Your name'}
                  autoComplete="off"
                  style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${cardBorder}`, background: light ? '#F9F9F9' : 'rgba(255,255,255,0.08)', color: textColor, fontSize: 15, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', direction: dir }}
                />
              </div>

              {/* Phone */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: light ? '#555' : 'rgba(255,255,255,0.6)', marginBottom: 5, fontFamily: 'Cairo, sans-serif' }}>
                  📱 {isAr ? 'رقم الهاتف' : 'Phone Number'}
                </label>
                <input
                  type="tel"
                  value={orderPhone}
                  onChange={e => setOrderPhone(e.target.value)}
                  placeholder={isAr ? '01xxxxxxxxx' : '01xxxxxxxxx'}
                  autoComplete="off"
                  style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${cardBorder}`, background: light ? '#F9F9F9' : 'rgba(255,255,255,0.08)', color: textColor, fontSize: 15, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', direction: 'ltr', textAlign: isAr ? 'right' : 'left' }}
                />
              </div>

              {/* Address */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: light ? '#555' : 'rgba(255,255,255,0.6)', marginBottom: 5, fontFamily: 'Cairo, sans-serif' }}>
                  🏠 {isAr ? 'العنوان' : 'Address'}
                </label>
                <textarea
                  value={orderAddress}
                  onChange={e => setOrderAddress(e.target.value)}
                  placeholder={isAr ? 'عنوانك بالتفصيل' : 'Your delivery address'}
                  rows={2}
                  autoComplete="off"
                  style={{ width: '100%', padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${cardBorder}`, background: light ? '#F9F9F9' : 'rgba(255,255,255,0.08)', color: textColor, fontSize: 15, fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box', resize: 'none', direction: dir }}
                />
              </div>

              {/* Confirm button */}
              {(() => {
                const target = orderBranch || (waBranches.length === 1 ? waBranches[0] : null);
                if (!target || !target.whatsapp) return null;
                const waUrl = `https://wa.me/${target.whatsapp.replace(/\D/g, '')}?text=${buildWAMsg(orderName, orderAddress, orderPhone)}`;
                return (
                  <a
                    href={waUrl}
                    target="_blank" rel="noreferrer"
                    onClick={() => {
                      localStorage.setItem('pm_order_name', orderName);
                      localStorage.setItem('pm_order_phone', orderPhone);
                      localStorage.setItem('pm_order_address', orderAddress);
                      setShowOrderForm(false);
                      setShowBasket(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '15px 0', borderRadius: 16, background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none', fontFamily: 'Cairo, sans-serif' }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {isAr ? 'تاكيد وارسال الطلب' : 'Confirm & Send Order'}
                  </a>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ===== BOTTOM HOME BUTTON ===== */}
      {/* Floating circular button at the bottom-middle of the public menu.
          Click behavior:
            • In grid drill-down (viewing one category's items) → returns
              to the grid tile-browser (all categories).
            • Otherwise → smooth-scrolls to the top of the page.
          Sits above the basket bar so it never overlaps the order CTA. */}
      {!showBasket && !showOrderForm && !detailItem && (
        <button
          onClick={() => {
            if (viewLayout === 'grid' && gridCatId) {
              setGridCatId(null);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          aria-label={isAr ? 'الصفحة الرئيسية' : 'Home'}
          title={isAr ? 'الصفحة الرئيسية' : 'Home'}
          style={{
            position: 'fixed',
            bottom: basketCount > 0 ? 78 : 18,   // hop above the basket bar when it's showing
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 501,
            width: 52, height: 52, borderRadius: '50%',
            border: 'none',
            background: accentColor,
            color: '#fff',
            fontSize: 24,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
            transition: 'transform 0.18s ease, bottom 0.22s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-50%) scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(-50%) scale(1)'; }}>
          🏠
        </button>
      )}

      {/* ===== BASKET FOOTER BAR ===== */}
      {basketCount > 0 && !showBasket && (
        <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, zIndex: 500, padding: '10px 16px 16px', background: `linear-gradient(0deg, ${bgColor} 70%, transparent)`, pointerEvents: 'none' }}>
          <button
            onClick={() => setShowBasket(true)}
            style={{ width: '100%', height: 54, borderRadius: 16, border: 'none', background: accentColor, color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px', fontFamily: 'Cairo, sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,0.25)', pointerEvents: 'all' }}>
            <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: 10, padding: '2px 10px', fontSize: 14, fontWeight: 800 }}>{basketCount}</span>
            <span>🛒 {isAr ? 'عرض الطلب' : 'View Order'}</span>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{formatPrice(basketTotal)} {currency}</span>
          </button>
        </div>
      )}

    </div>
  );
}
